import { AccountAccessStatus, AccountMember, AccountMemberRole, InvitationStatus, UserGroup } from '@celum/authentication';
import { AVATAR_SIZE, AvatarConfigBuilder, AvatarConfiguration, AvatarLabel } from '@celum/common-components/avatar';
import { ColorConstants } from '@celum/common-components/constants';
import { IconConfiguration } from '@celum/common-components/icon';
import { AccountUser, UserState } from '@celum/shared/domain';

const PENDING_BACKGROUND_COLOR = 'rgba(244, 99, 25, 0.08)';
const PENDING_BORDER_COLOR = '#f46319';
const REJECTED_BACKGROUND_COLOR = '#fff0f0';
const REJECTED_BORDER_COLOR = '#FF3F3F';
const LABEL_PREFIX = 'SHARED.USER_AVATAR';

export interface AccountUserToAvatarOptions {
  accountAccessToken?: string;
  tooltipText: string;

  size?: AVATAR_SIZE;
  fontSize?: number;
  showTooltip?: boolean;
  showDisplayName?: boolean;
  invitedTooltip?: string;
  rejectedTooltip?: string;
  deactivatedTooltip?: string;
  managerBadgeTooltip?: string;
  isManager?: boolean;
}

export interface UserGroupToAvatarOptions {
  size?: AVATAR_SIZE;
  fontSizeRem?: number;
  showTooltip?: boolean;
  clazz?: string;

  isManager?: boolean;
  managerBadgeTooltip?: string;
}

export function accountUserOrMemberToAvatar(userOrMember: Partial<AccountUser | AccountMember>, options: AccountUserToAvatarOptions): AvatarConfiguration {
  return accountUserOrMemberToAvatarBuilder(userOrMember, options).build();
}

export function accountUserOrMemberToAvatarBuilder(
  userOrMember: Partial<AccountUser | AccountMember>,
  options: AccountUserToAvatarOptions
): AvatarConfigBuilder<AvatarConfiguration> {
  const defaultOptions = { size: AVATAR_SIZE.m, showTooltip: true, showDisplayName: false, ...options };

  let userAvatarConfigBuilder: AvatarConfigBuilder<AvatarConfiguration>;

  // Determine status and invitationStatus fields
  const status = (userOrMember as AccountUser)?.status ?? (userOrMember as AccountMember)?.status;
  const invitationStatus = userOrMember?.invitationStatus;
  const isAPIMember = (userOrMember as AccountMember)?.effectiveRole === AccountMemberRole.API;

  if (!userOrMember || status === UserState.DELETED) {
    userAvatarConfigBuilder = deletedAvatarConfig(defaultOptions.size, defaultOptions.showTooltip);
  } else if (status === UserState.INACTIVE || status === AccountAccessStatus.INACTIVE) {
    userAvatarConfigBuilder = getInactiveConfig(userOrMember, defaultOptions);
  } else if (invitationStatus === InvitationStatus.INVITED) {
    userAvatarConfigBuilder = getInvitedConfig(userOrMember, defaultOptions);
  } else if (invitationStatus === InvitationStatus.PENDING_APPROVAL) {
    userAvatarConfigBuilder = getPendingConfig(userOrMember, defaultOptions);
  } else if (invitationStatus === InvitationStatus.REJECTED) {
    userAvatarConfigBuilder = getRejectedConfig(userOrMember, defaultOptions);
  } else if (invitationStatus === InvitationStatus.DISAPPROVED) {
    userAvatarConfigBuilder = getDisapprovedConfig(userOrMember, defaultOptions);
  } else {
    userAvatarConfigBuilder = getActiveConfig(userOrMember, defaultOptions);
  }

  if (options.isManager) {
    userAvatarConfigBuilder.withTopLeftLabel(new AvatarLabel('moderator-m', options.managerBadgeTooltip, '#794be8'));
  }

  if (isAPIMember) {
    userAvatarConfigBuilder = adjustForAPIConfig(userOrMember, status, userAvatarConfigBuilder, defaultOptions);
  }

  if (options.fontSize) {
    userAvatarConfigBuilder.withFontSizeInRem(options.fontSize);
  }

  return userAvatarConfigBuilder.withSize(defaultOptions.size).withEmail(userOrMember?.email);
}

export function deletedAvatarConfig(size: AVATAR_SIZE, showTooltip: boolean): AvatarConfigBuilder<AvatarConfiguration> {
  return new AvatarConfigBuilder()
    .withBackgroundColor(ColorConstants.SYSTEM_WHITE)
    .withDisplayName(`${LABEL_PREFIX}.DELETED`, false)
    .withIcon(new IconConfiguration('user-removed').withIconSize(size).withColor(ColorConstants.BLUE_GRAY_400))
    .withTooltip(showTooltip ? `${LABEL_PREFIX}.DELETED` : null);
}

export function userGroupToAvatarBuilder(group: UserGroup, options: UserGroupToAvatarOptions = {}): AvatarConfigBuilder<AvatarConfiguration> {
  const { size = AVATAR_SIZE.m, showTooltip = true, clazz, fontSizeRem } = options;
  const name = group.name;
  const builder = new AvatarConfigBuilder()
    .withBackgroundColor(ColorConstants.BLUE_GRAY_600)
    .withAvatarTextPlaceholder(name?.trim()[0].toUpperCase())
    .withDisplayText(name)
    .withTooltip(showTooltip ? name : null)
    .withSize(size)
    .withFontWeight(700)
    .withFontSizeInRem(fontSizeRem ?? 1.6)
    .withClass(clazz ?? 'group-avatar');

  if (options.isManager) {
    builder.withTopLeftLabel(new AvatarLabel('moderator-m', options.managerBadgeTooltip, '#794be8'));
  }

  return builder;
}

function getPendingConfig(user: Partial<AccountUser | AccountMember>, options: AccountUserToAvatarOptions): AvatarConfigBuilder<AvatarConfiguration> {
  return new AvatarConfigBuilder()
    .withBackgroundColor(PENDING_BACKGROUND_COLOR)
    .withBorderColor(PENDING_BORDER_COLOR)
    .withDisplayName(`${user.firstName} ${user.lastName}`, options.showDisplayName)
    .withImage(profilePictureSource(user, options.accountAccessToken))
    .withTextColor(PENDING_BORDER_COLOR)
    .withTooltip(options.showTooltip ? `${LABEL_PREFIX}.PENDING` : null, { name: `${user.firstName} ${user.lastName}` });
}

function getRejectedConfig(user: Partial<AccountUser | AccountMember>, options: AccountUserToAvatarOptions): AvatarConfigBuilder<AvatarConfiguration> {
  // Special case: The icons strokes are too thin for small sizes, so we'll have to use 2 different icons depending on the size
  const smallerIcon = options.size <= AVATAR_SIZE.m;
  const icon = smallerIcon ? 'invite' : 'invite-thin-rejected';
  const backgroundColor = smallerIcon ? REJECTED_BACKGROUND_COLOR : ColorConstants.SYSTEM_WHITE;

  return new AvatarConfigBuilder()
    .withBackgroundColor(backgroundColor)
    .withIcon(new IconConfiguration(icon).withIconSize(options.size - 2).withColor(REJECTED_BORDER_COLOR))
    .withImage(profilePictureSource(user, options.accountAccessToken))
    .withOpacity(0.8)
    .withTooltip(options.showTooltip ? (options.rejectedTooltip ?? `${LABEL_PREFIX}.REJECTED`) : null, { email: user.email });
}

function getDisapprovedConfig(user: Partial<AccountUser | AccountMember>, options: AccountUserToAvatarOptions): AvatarConfigBuilder<AvatarConfiguration> {
  return new AvatarConfigBuilder()
    .withBackgroundColor(REJECTED_BACKGROUND_COLOR)
    .withBorderColor(ColorConstants.ERROR)
    .withDisplayName(`${user.firstName} ${user.lastName}`, options.showDisplayName)
    .withTextColor(ColorConstants.ERROR)
    .withImage(profilePictureSource(user, options.accountAccessToken))
    .withTooltip(options.showTooltip ? `${LABEL_PREFIX}.DISAPPROVED` : null, { email: user.email });
}

function getInvitedConfig(user: Partial<AccountUser | AccountMember>, options: AccountUserToAvatarOptions): AvatarConfigBuilder<AvatarConfiguration> {
  return new AvatarConfigBuilder()
    .withBackgroundColor(ColorConstants.BLUE_GRAY_050)
    .withDisplayText(user.email)
    .withIcon(new IconConfiguration('invite').withIconSize(options.size - 2).withColor(ColorConstants.BLUE_GRAY_900))
    .withImage(profilePictureSource(user, options.accountAccessToken))
    .withOpacity(0.8)
    .withTooltip(options.showTooltip ? (options.invitedTooltip ?? `${LABEL_PREFIX}.INVITED`) : null, { email: user.email });
}

function getInactiveConfig(user: Partial<AccountUser | AccountMember>, options: AccountUserToAvatarOptions): AvatarConfigBuilder<AvatarConfiguration> {
  return new AvatarConfigBuilder()
    .withBackgroundColor(ColorConstants.BLUE_GRAY_400)
    .withBorderColor(ColorConstants.BLUE_GRAY_900)
    .withDisplayName(`${user.firstName} ${user.lastName}`, options.showDisplayName)
    .withImage(profilePictureSource(user, options.accountAccessToken))
    .withOpacity(0.36)
    .withTooltip(options.showTooltip ? (options.deactivatedTooltip ?? `${LABEL_PREFIX}.DEACTIVATED`) : null, { name: `${user.firstName} ${user.lastName}` });
}

function getActiveConfig(user: Partial<AccountUser | AccountMember>, options: AccountUserToAvatarOptions): AvatarConfigBuilder<AvatarConfiguration> {
  return new AvatarConfigBuilder()
    .withBackgroundColor(ColorConstants.BLUE_GRAY_400)
    .withDisplayName(`${user.firstName} ${user.lastName}`, options.showDisplayName)
    .withImage(profilePictureSource(user, options.accountAccessToken))
    .withTooltip(options.showTooltip ? options.tooltipText || `${user.firstName} ${user.lastName}` : null);
}

function adjustForAPIConfig(
  userOrMember: Partial<AccountUser | AccountMember>,
  status: AccountAccessStatus | UserState,
  userAvatarConfigBuilder: AvatarConfigBuilder<AvatarConfiguration>,
  options: AccountUserToAvatarOptions
): AvatarConfigBuilder<AvatarConfiguration> {
  userAvatarConfigBuilder
    .withIcon(new IconConfiguration('api-avatar').withColor(ColorConstants.SYSTEM_WHITE).withIconSize(options.size - options.size / 2.2))
    .withDisplayName('', false);

  if (status === AccountAccessStatus.ACTIVE) {
    userAvatarConfigBuilder
      .withBackgroundColor(ColorConstants.BLUE_GRAY_800)
      .withTooltip(options.showTooltip ? options.tooltipText || `${userOrMember.email}` : null);
  } else {
    userAvatarConfigBuilder.withTooltip(options.showTooltip ? `${LABEL_PREFIX}.DEACTIVATED` : null, { name: `${userOrMember.email}` });
  }

  return userAvatarConfigBuilder;
}

function profilePictureSource(user: Partial<AccountUser | AccountMember>, accountAccessToken: string): string {
  if (!user?.profilePictureDownloadLink) {
    return '';
  }
  return accountAccessToken
    ? `${user?.profilePictureDownloadLink}&type=preview_small&token=${accountAccessToken}`
    : `${user?.profilePictureDownloadLink}&type=preview_small`;
}
